"use client";

import { createHttpLink, ApolloLink, from } from "@apollo/client";
import { SSRMultipartLink, InMemoryCache, ApolloClient } from "@apollo/experimental-nextjs-app-support";

import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { NEXT_PUBLIC_AUTH_TOKEN, NEXT_PUBLIC_NODE_ENV } from "@/libs/constants/auth";
import { apiHostBaseUri } from "@/redux/slice/auth/apiHostBaseUri";

const isDevelopment = NEXT_PUBLIC_NODE_ENV === "development";

export default function buildApolloClient(
  authToken: string,
  tokenRefresh: boolean,
  fetchPatients: () => void,
  handleUnAuthenticated: () => void
) {
  const errorLink = onError((error) => {
    const { graphQLErrors, operation, forward } = error;

    if (isDevelopment) console.error(error);

    if (graphQLErrors) {
      const unauthenticatedError = graphQLErrors.some(
        (gqlError) =>
          gqlError.extensions &&
          (gqlError.extensions.code === "UNAUTHENTICATED" || gqlError.extensions.code === "AUTH_TOKEN_REVOKED")
      );
      if (unauthenticatedError) handleUnAuthenticated();
    }

    return forward(operation);
  });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      Authorization: authToken ? authToken : NEXT_PUBLIC_AUTH_TOKEN,
    },
  }));

  const httpLink = createHttpLink({
    uri: apiHostBaseUri(),
    fetchOptions: { next: { revalidate: 3600 } },
  });
  // use Apollo client from the client folder for the client side
  const link = from([errorLink, authLink, httpLink]);
  const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link:
      typeof window === "undefined"
        ? ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            link,
          ])
        : link,
  });

  if (authToken && !tokenRefresh) {
    fetchPatients();
  }

  return apolloClient;
}
